import type { ClothingItem, ClothingCategory, ClosetItem } from '@/types/closet'

/**
 * Raw closet item shape from ClosetContext. Mirrors ClosetItem but accepts a
 * looser `createdAt` (a Date or a serialized string from the API).
 */
type RawClosetItem = Omit<ClosetItem, 'createdAt'> & {
  createdAt?: Date | string
}

/**
 * Transform an API `ClothingItem` into the `ClosetItem` shape the ClosetContext
 * stores. Used by both the server-side seed (closet/page.tsx) and the client
 * refresh (ClosetContext.refreshItems) so the mapping lives in exactly one place.
 */
export function toClosetItem(item: ClothingItem): ClosetItem {
  return {
    id: item.id,
    name: item.name,
    category: item.category,
    color: item.color?.[0], // Take first color from array
    size: item.size,
    brand: item.brand,
    price: item.price,
    image: item.imageUrl, // Map imageUrl to image
    createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
  }
}

/**
 * Transform a raw closet item (from ClosetContext) into the ClothingItem type.
 * Used by both ClosetView and OutfitBuilder to avoid duplicated transformations.
 */
export function transformClosetItem(item: RawClosetItem): ClothingItem {
  const dateStr =
    item.createdAt instanceof Date
      ? item.createdAt.toISOString()
      : item.createdAt || new Date().toISOString()

  return {
    id: item.id,
    name: item.name,
    category: item.category as ClothingCategory,
    brand: item.brand,
    size: item.size,
    color: item.color ? [item.color] : [],
    imageUrl: item.image,
    tags: [],
    price: item.price,
    season: 'all' as const,
    createdAt: dateStr,
    updatedAt: dateStr,
  }
}
